import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getFullUrl } from '../config';
import MultiSelectDropdown from './MultiSelectDropdown';

function AccessoriesCatalogView({ 
  onTeleportToItem, 
  onShowItemDetails, 
  currentShowroomId, 
  onShowConfirmation,
  backgroundLoadingProgress,
  roomReadiness 
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    showroom: 'all',
    categories: [],
    search: ''
  });

  useEffect(() => {
    fetchCatalogData();
  }, []);

  const fetchCatalogData = async () => {
    try {
      const [itemsRes, categoriesRes, showroomsRes, placementsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/items`),
        fetch(`${API_BASE_URL}/categories`),
        fetch(`${API_BASE_URL}/showrooms`),
        fetch(`${API_BASE_URL}/placements`)
      ]);

      if (!itemsRes.ok || !categoriesRes.ok || !showroomsRes.ok || !placementsRes.ok) {
        throw new Error('Failed to fetch catalog data');
      }

      const [itemsData, categoriesData, showroomsData, placementsData] = await Promise.all([
        itemsRes.json(),
        categoriesRes.json(),
        showroomsRes.json(),
        placementsRes.json()
      ]);

      // Filter items to only show category_id = 2
      const accessoryItems = itemsData.filter(item => 
        (item.categoryIds || []).includes(2)
      );

      // Filter categories to only show categories with id > 2
      const accessoryCategories = categoriesData.filter(category => category.id > 2);

      setItems(accessoryItems);
      setCategories(accessoryCategories);
      setShowrooms(showroomsData.filter(room => room.enabled !== false));
      setPlacements(placementsData);
    } catch (err) {
      console.error('Failed to fetch catalog data:', err);
      setError('Failed to load accessories catalog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getItemShowrooms = (itemId) => {
    const itemPlacements = placements.filter(p => p.itemId === itemId);
    return itemPlacements.map(p => {
      const showroom = showrooms.find(s => s.id === p.showroomId);
      return showroom ? { ...showroom, placement: p } : null;
    }).filter(Boolean);
  };

  const getRoomStatus = (roomId) => {
    if (!roomReadiness || !roomReadiness.has(roomId)) {
      return { ready: false, loading: false, error: false };
    }
    
    const state = roomReadiness.get(roomId);
    return {
      ready: state.loaded && !state.loading && !state.error,
      loading: state.loading,
      error: state.error
    };
  };

  const getLoadingPercentage = (roomId) => {
    // Since we don't have per-room loading percentage, 
    // we'll show overall progress for loading rooms
    if (backgroundLoadingProgress && backgroundLoadingProgress.total > 0) {
      const progress = (backgroundLoadingProgress.loaded / backgroundLoadingProgress.total) * 100;
      return Math.round(progress);
    }
    return 0;
  };

  const handleTeleportToItem = (item) => {
    const itemShowrooms = getItemShowrooms(item.id);
    
    if (itemShowrooms.length === 0) {
      alert('This item is not currently placed in any showroom.');
      return;
    }

    // Find if item is in current showroom
    const currentRoomPlacement = itemShowrooms.find(s => s.id === currentShowroomId);
    
    if (currentRoomPlacement) {
      // Item is in current room, teleport directly
      onTeleportToItem(item, currentRoomPlacement.placement);
    } else {
      // Item is in different room(s), show confirmation
      const targetShowroom = itemShowrooms[0];
      const currentRoom = showrooms.find(r => r.id === currentShowroomId);
      onShowConfirmation(
        `${t('teleportToItem')} "${item.name}"?`,
        `"${item.name}" ${t('itemLocation')} "${targetShowroom.name}". ${t('currentlyInRoom')} "${currentRoom?.name}". ${t('switchRooms')}`,
        () => onTeleportToItem(item, targetShowroom.placement, targetShowroom.id)
      );
    }
  };

  const filteredItems = items.filter(item => {
    const itemShowrooms = getItemShowrooms(item.id);
    
    // Filter by showroom
    if (filters.showroom !== 'all') {
      const isInSelectedShowroom = itemShowrooms.some(s => s.id === parseInt(filters.showroom));
      if (!isInSelectedShowroom) return false;
    }

    // Filter by categories (id > 2) - ALL selected categories must match (AND logic)
    if (filters.categories.length > 0) {
      const itemCategoryIds = item.categoryIds || [];
      const hasAllCategories = filters.categories.every(categoryId => 
        itemCategoryIds.includes(categoryId)
      );
      if (!hasAllCategories) return false;
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // Category filter handler
  const handleCategoryChange = (selectedCategories) => {
    setFilters(prev => ({
      ...prev,
      categories: selectedCategories
    }));
  };

  // Prepare category options with item counts
  const categoryOptions = categories.map(category => ({
    ...category,
    itemCount: items.filter(item => 
      (item.categoryIds || []).includes(category.id)
    ).length
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={fetchCatalogData}
          className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-black"
        >
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Каталог магазина аксессуаров</h2>
      </div>

      {/* Mobile-first compact filters */}
      <div className="mb-4">
        {/* Search - full width on mobile */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Поиск аксессуаров..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded text-base"
          />
        </div>

        {/* Filters - stacked on mobile, side by side on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <select
            value={filters.showroom}
            onChange={(e) => setFilters({ ...filters, showroom: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded text-base bg-white"
          >
            <option value="all">{t('allExpositions')}</option>
            {showrooms.map(showroom => (
              <option key={showroom.id} value={showroom.id}>
                {showroom.name}
              </option>
            ))}
          </select>
        </div>

        {/* Category Filtering - only categories with id > 2 */}
        {categories.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-800 mb-2">
              {t('categories')}
            </label>
            <MultiSelectDropdown
              options={categoryOptions}
              selected={filters.categories}
              onChange={handleCategoryChange}
              placeholder="Выберите дополнительные категории..."
              className="w-full"
            />
          </div>
        )}

        {/* Clear filters button */}
        {(filters.search || filters.showroom !== 'all' || filters.categories.length > 0) && (
          <div className="mt-4">
            <button
              onClick={() => setFilters({ showroom: 'all', categories: [], search: '' })}
              className="px-3 py-2 bg-gray-600 text-white rounded text-base hover:bg-gray-700"
            >
              {t('clearFilters')}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 text-base text-gray-600">
        {filteredItems.length} {filteredItems.length === 1 ? 'аксессуар' : 'аксессуаров'}
      </div>

      {/* Mobile-optimized Items Grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-gray-400 text-4xl mb-4">🛍️</div>
          <div>Аксессуары не найдены</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const itemShowrooms = getItemShowrooms(item.id);
            
            return (
              <div
                key={item.id}
                className="border border-gray-200 rounded overflow-hidden hover:border-gray-300 transition-colors bg-white"
              >
                {/* Item Image - responsive aspect ratio */}
                <div 
                  className="aspect-square bg-gray-100 flex items-center justify-center cursor-pointer overflow-hidden"
                  onClick={() => onShowItemDetails(item, itemShowrooms)}
                >
                  {item.thumbnailUrl ? (
                    // Show thumbnail image for 3D items
                    <img 
                      src={getFullUrl(item.thumbnailUrl)} 
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  ) : item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf')) ? (
                    // For 3D items without thumbnail, show a better placeholder
                    <div className="text-center p-4">
                      <div className="text-gray-400 text-4xl mb-2">🏺</div>
                      <div className="text-xs text-gray-500 font-medium">3D Model</div>
                      <div className="text-xs text-gray-400 mt-1">Click to view</div>
                    </div>
                  ) : item.url ? (
                    <img 
                      src={getFullUrl(item.url)} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-3xl">🛍️</div>
                  )}
                </div>

                {/* Item Info - compact mobile layout */}
                <div className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <h3 
                      className="font-medium text-gray-900 cursor-pointer hover:text-black text-base leading-tight"
                      onClick={() => onShowItemDetails(item, itemShowrooms)}
                    >
                      {item.name}
                    </h3>
                    <span className="text-sm text-gray-500 px-2 py-1 bg-gray-100 rounded ml-2 flex-shrink-0">
                      {item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf')) ? '3D' : 'Аксессуар'}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {item.description}
                  </p>

                  {/* Categories */}
                  {item.categoryNames && item.categoryNames.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.categoryNames.slice(0, 3).map((categoryName) => (
                        <span key={categoryName} className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                          {categoryName}
                        </span>
                      ))}
                      {item.categoryNames.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                          +{item.categoryNames.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Exposition Info - compact */}
                  {itemShowrooms.length > 0 && (
                    <div className="text-sm text-gray-500 mb-3">
                      📍 {itemShowrooms[0]?.name}{itemShowrooms.length > 1 && ` +${itemShowrooms.length - 1} ${t('more')}`}
                    </div>
                  )}

                  {/* Actions - compact buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => onShowItemDetails(item, itemShowrooms)}
                      className="w-full px-3 py-2 text-base text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      {t('viewDetails')}
                    </button>
                    
                    {itemShowrooms.length > 0 && (() => {
                      const targetShowroom = itemShowrooms.find(s => s.id === currentShowroomId) || itemShowrooms[0];
                      const roomStatus = getRoomStatus(targetShowroom.id);
                      const isCurrentRoom = targetShowroom.id === currentShowroomId;
                      
                      if (roomStatus.ready || isCurrentRoom) {
                        return (
                          <button
                            onClick={() => handleTeleportToItem(item)}
                            className="w-full px-3 py-2 text-base bg-gray-900 text-white rounded hover:bg-black"
                          >
                            {t('teleportToItem')}
                          </button>
                        );
                      } else if (roomStatus.error) {
                        return (
                          <button
                            disabled
                            className="w-full px-3 py-2 text-base bg-red-100 text-red-600 rounded cursor-not-allowed"
                          >
                            Ошибка загрузки
                          </button>
                        );
                      } else {
                        const loadingPercentage = getLoadingPercentage(targetShowroom.id);
                        return (
                          <button
                            disabled
                            className="w-full px-3 py-2 text-base bg-gray-100 text-gray-600 rounded cursor-not-allowed flex items-center justify-between"
                          >
                            <span>Готовим к показу</span>
                            <span className="text-sm">{loadingPercentage}%</span>
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AccessoriesCatalogView;